import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Home as HomeIcon } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

/** Convert a YouTube / Vimeo / direct-video URL into an embeddable iframe URL. */
function toEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return { type: "iframe", src: `https://www.youtube.com/embed/${v}` };
      // youtube.com/embed/<id> already
      if (u.pathname.startsWith("/embed/")) {
        return { type: "iframe", src: url };
      }
    }
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace(/^\//, "").split("?")[0];
      if (id) return { type: "iframe", src: `https://www.youtube.com/embed/${id}` };
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.replace(/^\//, "").split("?")[0];
      if (id) return { type: "iframe", src: `https://player.vimeo.com/video/${id}` };
    }
    // Direct mp4/webm
    if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(u.pathname)) {
      return { type: "video", src: url };
    }
  } catch {
    /* ignore malformed URL */
  }
  return { type: "iframe", src: url };
}

/**
 * Public-facing tab strip for the dealer home page.
 *
 *  • First tab is always "🏠 Home" — renders the dealer's HTML widget
 *    (`home_html`). Tab is hidden if the dealer hasn't set any HTML.
 *  • Remaining tabs come from the resolved Posts list (dealer override or
 *    admin defaults from Posts.Home checkbox).
 *  • Active tab shows the post's video embed (when present) + sanitized HTML.
 */
export default function PostTabs({ homeHtml = "", posts = [], dealerName = "", dealerSlug = "" }) {
  const hasHome = !!(homeHtml || "").trim();

  const items = useMemo(() => {
    const list = [];
    const homeItem = { key: "__home__", slug: "home", title: "Home", isHome: true, html: homeHtml };
    const hasMarker = (posts || []).some((p) => p.is_home);
    if (hasHome && !hasMarker) list.push(homeItem);
    for (const p of posts || []) {
      if (p.is_home) {
        if (hasHome) list.push(homeItem);
        continue;
      }
      list.push({
        key: p.slug || p.title,
        slug: p.slug,
        title: p.title || p.slug,
        video_url: p.video_url,
        // Product cards inserted from the catalogue link to THIS dealer's store.
        html: dealerSlug ? (p.html || "").replace(/href="\/d\/[^/"]+\/product\//g, `href="/d/${dealerSlug}/product/`) : p.html,
      });
    }
    return list;
  }, [hasHome, homeHtml, posts, dealerSlug]);

  const [activeKey, setActiveKey] = useState(items[0]?.key);
  const active = items.find((i) => i.key === activeKey) || items[0];
  useEffect(() => {
    const onOpen = (e) => {
      const hit = items.find((i) => i.slug === e.detail);
      if (hit) setActiveKey(hit.key);
    };
    window.addEventListener("postcue:open-post", onOpen);
    return () => window.removeEventListener("postcue:open-post", onOpen);
  }, [items]);

  if (!items.length) return null;

  return (
    <section data-testid="dealer-post-tabs" className="space-y-3">
      {/* Tab strip */}
      <div
        className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1"
        role="tablist"
        aria-label={dealerName ? `${dealerName} home tabs` : "Home tabs"}
      >
        {items.map((it) => {
          const isActive = it.key === active?.key;
          return (
            <button
              key={it.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveKey(it.key)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-5 h-10 rounded-full text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "bg-white text-zinc-700 border border-zinc-200 hover:border-emerald-300 hover:text-emerald-700"
              }`}
              data-testid={`dealer-tab-${it.slug}`}
            >
              {it.isHome && <HomeIcon size={12} />}
              {it.title}
            </button>
          );
        })}
      </div>

      {/* Active tab content */}
      {active && (
        <Card
          className="rounded-2xl border-zinc-200 overflow-hidden"
          data-testid={`dealer-tab-content-${active.slug}`}
        >
          {!active.isHome && active.video_url && (
            <VideoEmbed url={active.video_url} title={active.title} />
          )}
          {active.html ? (
            active.isHome && looksLikeFullDoc(active.html) ? (
              // Full-document HTML (admin pasted a complete <!DOCTYPE html>
              // with its own <style> block). Render inside a sandboxed
              // iframe so the inline styles + fonts win and our Tailwind
              // resets don't bleed in.
              <HtmlFrame html={active.html} title={active.title} />
            ) : (
              <div
                className="prose prose-sm max-w-none text-zinc-800 p-5"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(active.html),
                }}
              />
            )
          ) : null}
          {!active.html && !active.video_url && (
            <div className="p-6 text-center text-sm text-zinc-500">
              {active.title} — no content yet.
            </div>
          )}
        </Card>
      )}
    </section>
  );
}

function looksLikeFullDoc(html) {
  const s = (html || "").slice(0, 400).toLowerCase();
  return s.includes("<!doctype") || s.includes("<html") || s.includes("<style");
}

function HtmlFrame({ html, title }) {
  const ref = useRef(null);
  return (
    <iframe
      ref={ref}
      title={title || "Home"}
      srcDoc={html}
      sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
      referrerPolicy="no-referrer"
      className="w-full block border-0"
      style={{ height: "70vh" }}
      onLoad={() => {
        const f = ref.current;
        if (!f) return;
        try {
          const doc = f.contentDocument;
          const h = Math.max(
            doc.documentElement.scrollHeight,
            doc.body?.scrollHeight || 0,
          );
          if (h > 0) f.style.height = `${h + 4}px`;
        } catch {
          /* ignore */
        }
      }}
      data-testid="dealer-home-iframe"
    />
  );
}

function VideoEmbed({ url, title }) {
  const embed = toEmbed(url);
  if (!embed) return null;
  if (embed.type === "video") {
    return (
      <video
        src={embed.src}
        controls
        playsInline
        className="w-full bg-black aspect-video"
      />
    );
  }
  return (
    <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
      <iframe
        src={embed.src}
        title={title || "Video"}
        className="absolute inset-0 w-full h-full"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </div>
  );
}
